import { Check, Lock } from 'lucide-react';
import { PHASES } from '@/constants/phases';

/**
 * Bandeau horizontal de progression des 5 phases.
 * Utilisé dans DossierDetailPage.
 */
export default function PhaseProgressBar({ dossier, phaseProgress }) {
  const phasesValidees = dossier?.phases_validees || [];
  const currentPhase = dossier?.phase || 1;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-900">
          Progression du dossier
        </h2>
        <span className="text-xs text-slate-500">
          Phase {currentPhase} sur {PHASES.length}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {PHASES.map((phase) => {
          const validated = phasesValidees.includes(phase.id);
          const active = phase.id === currentPhase;
          const locked = !validated && !active && phase.id > currentPhase;

          // Get progress from API response
          const progressData = phaseProgress?.find(
            (p) => p.phase === phase.id
          );
          const done = progressData?.sections_complete ?? 0;
          const total = progressData?.sections_total ?? phase.sections.length;
          const pct = total > 0 ? (done / total) * 100 : 0;

          // Styling
          let bgClass, borderClass, textClass, barClass;
          if (validated) {
            bgClass = 'bg-carex-50';
            borderClass = 'border-t-carex-600';
            textClass = 'text-carex-600';
            barClass = 'bg-carex-600';
          } else if (active) {
            bgClass = 'bg-orange-50';
            borderClass = 'border-t-orange-500';
            textClass = 'text-orange-600';
            barClass = 'bg-orange-500';
          } else {
            bgClass = 'bg-surface-muted';
            borderClass = 'border-t-slate-200';
            textClass = 'text-slate-400';
            barClass = 'bg-slate-300';
          }

          return (
            <div
              key={phase.id}
              className={`${bgClass} rounded-lg p-3.5 border-t-[3px] ${borderClass} ${locked ? 'opacity-50' : ''}`}
            >
              {/* Phase header */}
              <div className="flex items-center gap-1.5 mb-1.5">
                {validated ? (
                  <div className="w-5 h-5 bg-carex-600 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                ) : locked ? (
                  <div className="w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center">
                    <Lock className="w-2.5 h-2.5 text-slate-400" />
                  </div>
                ) : (
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      active ? 'bg-orange-500' : 'bg-slate-300'
                    }`}
                  >
                    <span className="text-[10px] font-bold text-white">
                      {phase.id}
                    </span>
                  </div>
                )}
                <span className={`text-xs font-semibold ${textClass}`}>
                  {phase.id}. {phase.shortLabel}
                </span>
              </div>

              {/* Section list */}
              <div className="text-[10px] text-slate-500 leading-relaxed mb-2">
                {phase.sections.map((s) => s.label).join(' · ')}
              </div>

              {/* Progress bar */}
              <div className="h-[3px] bg-slate-200 rounded-full">
                <div
                  className={`h-full ${barClass} rounded-full transition-all duration-300`}
                  style={{ width: `${validated ? 100 : pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
